import { useEffect, useState, type FormEvent } from "react";
import { apiGet, apiSend } from "../../lib/api";

type AdminUser = {
  email: string;
  display_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  last_login_at: string | null;
  modules: { module_slug: string }[];
};

// `admin` and `reports` are role-based (admin-only), not grantable modules.
const MODULES = ["sms", "voice", "chat", "food", "members"];
const DEFAULT_TRIBALAXE_GRANTS = ["sms", "voice", "members"] as const;

export function AdminPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);

  async function load() {
    try {
      setUsers(await apiGet<AdminUser[]>("/admin/users"));
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => { load(); }, []);

  async function toggleModule(email: string, slug: string, currently: boolean) {
    setBusy(`${email}:${slug}`);
    try {
      await apiSend<void>(
        `/admin/users/${encodeURIComponent(email)}/modules/${slug}`,
        currently ? "DELETE" : "POST"
      );
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function toggleAdmin(email: string, currently: boolean) {
    setBusy(`${email}:admin`);
    try {
      await apiSend<void>(
        `/admin/users/${encodeURIComponent(email)}/is-admin`,
        "PUT",
        { is_admin: !currently }
      );
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function resetAddForm() {
    setNewEmail("");
    setNewDisplayName("");
    setNewIsAdmin(false);
    setAddErr(null);
  }

  async function submitAdd(e: FormEvent) {
    e.preventDefault();
    setAddErr(null);

    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAddErr("Enter a valid email address.");
      return;
    }

    setAddBusy(true);
    try {
      await apiSend<unknown>("/admin/users", "POST", {
        email,
        display_name: newDisplayName.trim() || undefined,
        is_admin: newIsAdmin,
        is_active: true,
      });

      // For a non-admin @tribalaxe.com email, apply the same default grants
      // the 0001/0005 triggers would apply on first sign-in. Admins get every
      // module implicitly via `hq_me`, so no grants needed for them.
      if (!newIsAdmin && email.endsWith("@tribalaxe.com")) {
        for (const slug of DEFAULT_TRIBALAXE_GRANTS) {
          try {
            await apiSend<void>(
              `/admin/users/${encodeURIComponent(email)}/modules/${slug}`,
              "POST"
            );
          } catch (grantErr) {
            console.warn("default grant failed", slug, (grantErr as Error).message);
          }
        }
      }

      await load();
      resetAddForm();
      setAddOpen(false);
    } catch (e) {
      setAddErr((e as Error).message);
    } finally {
      setAddBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Admin — Users & access</h1>
          <p className="mt-1 text-sm text-tribal-muted">
            New @tribalaxe.com signups auto-get{" "}
            <span className="font-medium text-white">SMS + Voice + Membership</span>. Grant more here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setAddOpen((v) => !v); if (!addOpen) resetAddForm(); }}
          className="shrink-0 rounded-md bg-tribal-red px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red/90"
        >
          {addOpen ? "Cancel" : "Add user"}
        </button>
      </div>

      {addOpen && (
        <form
          onSubmit={submitAdd}
          className="mt-4 rounded-lg border border-tribal-border bg-tribal-surface p-4"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-tribal-muted">
                Email
              </span>
              <input
                type="email"
                required
                autoFocus
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="someone@tribalaxe.com"
                className="mt-1 block w-full rounded-md border border-tribal-border bg-black/40 px-3 py-2 text-sm text-white placeholder:text-tribal-muted/60 focus:border-tribal-red focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-tribal-muted">
                Display name (optional)
              </span>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1 block w-full rounded-md border border-tribal-border bg-black/40 px-3 py-2 text-sm text-white placeholder:text-tribal-muted/60 focus:border-tribal-red focus:outline-none"
              />
            </label>
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-tribal-muted">
            <input
              type="checkbox"
              checked={newIsAdmin}
              onChange={(e) => setNewIsAdmin(e.target.checked)}
              className="h-4 w-4 rounded border-tribal-border bg-black/40 accent-tribal-red"
            />
            <span>Grant admin (sees every module implicitly)</span>
          </label>

          <p className="mt-2 text-xs text-tribal-muted">
            Non-admin <span className="text-white">@tribalaxe.com</span> users are granted{" "}
            <span className="text-white">SMS + Voice + Membership</span> automatically. Other
            emails are added with no grants — use the toggles below to enable modules.
          </p>

          {addErr && (
            <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {addErr}
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setAddOpen(false); resetAddForm(); }}
              disabled={addBusy}
              className="rounded-md px-3 py-2 text-sm text-tribal-muted transition-colors hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addBusy}
              className="rounded-md bg-tribal-red px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red/90 disabled:opacity-60"
            >
              {addBusy ? "Saving…" : "Save user"}
            </button>
          </div>
        </form>
      )}

      {err && <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">{err}</div>}

      <div className="mt-6 overflow-x-auto rounded-lg border border-tribal-border bg-tribal-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-tribal-border text-left text-xs uppercase tracking-wide text-tribal-muted">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Admin</th>
              {MODULES.map((m) => (
                <th key={m} className="px-2 py-3 text-center">{m}</th>
              ))}
              <th className="px-4 py-3">Last login</th>
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map((u) => (
              <tr key={u.email} className="border-b border-tribal-border/60 last:border-0">
                <td className="px-4 py-2">
                  <div className="font-medium">{u.display_name ?? u.email}</div>
                  <div className="text-xs text-tribal-muted">{u.email}</div>
                </td>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => toggleAdmin(u.email, u.is_admin)}
                    disabled={busy === `${u.email}:admin`}
                    className={`rounded px-2 py-1 text-xs font-medium ${u.is_admin ? "bg-tribal-red text-white" : "bg-white/5 text-tribal-muted hover:text-white"}`}
                  >
                    {u.is_admin ? "admin" : "user"}
                  </button>
                </td>
                {MODULES.map((slug) => {
                  const on = u.is_admin || u.modules.some((x) => x.module_slug === slug);
                  return (
                    <td key={slug} className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleModule(u.email, slug, on && !u.is_admin)}
                        disabled={u.is_admin || busy === `${u.email}:${slug}`}
                        aria-label={`${on ? "Revoke" : "Grant"} ${slug} for ${u.email}`}
                        className={`h-6 w-10 rounded-full transition-colors ${
                          on ? "bg-tribal-red" : "bg-white/10"
                        } ${u.is_admin ? "opacity-60" : "hover:opacity-90"}`}
                      >
                        <span
                          className={`block h-5 w-5 transform rounded-full bg-white transition-transform ${
                            on ? "translate-x-5" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-xs text-tribal-muted">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
